import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { ActionBarConfig, ActionBarFormButton, ActionBarScheduleButton, isActionBarChatButton } from "@xapp/stentor-chat-widget";
import ActionBarButton from "./ActionBarButton";
import ActionBarCta from "./ActionBarCta";

import "./ActionBar.scss";

export interface ActionBarProps {
    readonly config: ActionBarConfig;
    /**
     * Whether the chat widget is currently visible/open
     */
    readonly visible: boolean;
    /**
     * Whether the chat widget is disabled (chat buttons should be hidden)
     */
    readonly chatDisabled?: boolean;
    /**
     * Whether the user has already interacted with the widget
     * (e.g., clicked chat button, sent a message, dismissed CTA)
     */
    readonly hasUserInteracted?: boolean;
    /**
     * Called when the chat button is clicked to open chat
     */
    readonly onChatClick: () => void;
    /**
     * Called when the chat button is clicked to close/minimize chat
     */
    readonly onChatMinimize: () => void;
    /**
     * Called when form or schedule button is clicked
     */
    readonly onFormClick: (button: ActionBarFormButton | ActionBarScheduleButton) => void;
    /**
     * Called when the CTA is dismissed
     */
    readonly onCtaDismiss?: () => void;
}

const DEFAULT_MOBILE_BREAKPOINT = 768;
const RESIZE_DEBOUNCE_MS = 150;

const ActionBar: FC<ActionBarProps> = ({
    config,
    visible,
    chatDisabled,
    hasUserInteracted = false,
    onChatClick,
    onChatMinimize,
    onFormClick,
    onCtaDismiss,
}) => {
    const [isMobile, setIsMobile] = useState<boolean>(false);
    const mobileBreakpoint = config.mobileBreakpoint ?? DEFAULT_MOBILE_BREAKPOINT;
    const position = config.position ?? "right";
    const resizeTimeoutRef = useRef<number | null>(null);

    // Debounced resize handler
    const handleResize = useCallback(() => {
        if (resizeTimeoutRef.current) {
            window.clearTimeout(resizeTimeoutRef.current);
        }
        resizeTimeoutRef.current = window.setTimeout(() => {
            setIsMobile(window.innerWidth <= mobileBreakpoint);
        }, RESIZE_DEBOUNCE_MS);
    }, [mobileBreakpoint]);

    useEffect(() => {
        // Initial check (immediate, not debounced)
        setIsMobile(window.innerWidth <= mobileBreakpoint);

        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
            if (resizeTimeoutRef.current) {
                window.clearTimeout(resizeTimeoutRef.current);
            }
        };
    }, [mobileBreakpoint, handleResize]);

    // Filter out chat buttons if chat is disabled
    const filteredButtons = chatDisabled
        ? config.buttons.filter((button) => !isActionBarChatButton(button))
        : config.buttons;

    // Build class names - ActionBar stays visible even when chat is open
    const classNames = [
        "xapp-action-bar",
        `xapp-action-bar--${position}`,
        isMobile ? "xapp-action-bar--mobile" : "xapp-action-bar--desktop",
    ]
        .filter(Boolean)
        .join(" ");

    // Determine if CTA should be shown:
    // - Must have CTA config with a message
    // - Chat must not be visible (user hasn't opened it)
    // - Chat must not be disabled
    const hasChatButton = config.buttons.some(isActionBarChatButton);
    const showCta = config.cta?.message && !visible && !chatDisabled && hasChatButton;

    const handleCtaClick = () => {
        onChatClick();
    };

    const handleCtaDismiss = () => {
        onCtaDismiss?.();
    };

    return (
        <div className={classNames} role="toolbar" aria-label="Chat actions">
            {/* CTA banner floating above the action bar */}
            {showCta && (
                <div className="xapp-action-bar-cta-wrapper">
                    <ActionBarCta
                        config={config.cta}
                        isMobile={isMobile}
                        onClick={handleCtaClick}
                        onDismiss={handleCtaDismiss}
                        hasUserInteracted={hasUserInteracted}
                    />
                </div>
            )}
            <div className="xapp-action-bar__buttons">
                {filteredButtons.map((button) => (
                    <ActionBarButton
                        key={button.id}
                        button={button}
                        chatActive={visible}
                        onChatClick={onChatClick}
                        onChatMinimize={onChatMinimize}
                        onFormClick={onFormClick}
                        isMobile={isMobile}
                    />
                ))}
            </div>
        </div>
    );
};

export default ActionBar;
